import React from 'react'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { useTheme } from '@mui/material/styles'

const AdxButton = ({
  onClick,
  children,
  size,
  variant,
  isLoading,
  sx,
  disabled,
  textColor = 'text.primary',
  ...rest
}) => {
  const { typography } = useTheme()
  let fontSize
  let fontWeight = 500
  let minHeight
  let btnVariant
  let themeColor
  let hoverColor
  let spinnerColor
  let bgColorDisabled
  let themeColorDisabled
  let textThemeColor // new variable for text color

  switch (size) {
    case 'large':
      fontSize = typography.h3.fontSize
      minHeight = '65px'
      break
    case 'standard':
      fontSize = typography.subtitle1.fontSize
      minHeight = '45px'
      break
    case 'small':
      fontSize = typography.subtitle1.fontSize
      minHeight = '35px'
      break
    case 'tiny':
      fontSize = typography.subtitle1.fontSize
      minHeight = '25px'
      break
    default:
      fontSize = typography.subtitle1.fontSize
      minHeight = '45px'
  }

  switch (variant) {
    case 'contained':
      btnVariant = 'contained'
      hoverColor = 'primary.hover'
      spinnerColor = 'text'
      themeColor = 'primary'
      textThemeColor = textColor || 'white.main' // default white text for contained
      themeColorDisabled = 'white.main'
      break
    case 'contained-inverted':
      btnVariant = 'contained'
      hoverColor = 'white.main'
      spinnerColor = 'text'
      themeColor = 'white.main'
      themeColorDisabled = 'primary.main'
      break
    case 'contained-destructive':
      btnVariant = 'contained'
      hoverColor = 'error.main'
      spinnerColor = 'white'
      themeColor = 'error'
      themeColorDisabled = 'white.main'
      break
    case 'outlined':
      btnVariant = 'outlined'
      bgColorDisabled = 'transparent'
      spinnerColor = 'text'
      themeColor = 'primary'
      textThemeColor = textColor || 'text.primary' // default to primary color text
      themeColorDisabled = 'light-gray-text-2.main'
      break
    case 'outlined-inverted':
      btnVariant = 'outlined'
      bgColorDisabled = 'transparent'
      spinnerColor = 'white'
      themeColor = 'white'
      themeColorDisabled = 'light-gray-text-2.main'
      break
    case 'outlined-icon':
      btnVariant = 'outlined'
      bgColorDisabled = 'transparent'
      spinnerColor = 'primary'
      themeColor = 'primary'
      themeColorDisabled = 'light-gray-text-2.main'
      break
    case 'outlined-destructive':
      btnVariant = 'outlined'
      bgColorDisabled = 'transparent'
      spinnerColor = 'error'
      themeColor = 'error'
      themeColorDisabled = 'light-gray-text-2.main'
      break
    case 'ghost':
      btnVariant = 'text'
      hoverColor = 'error'
      spinnerColor = 'text'
      bgColorDisabled = 'transparent'
      themeColor = 'primary'
      textThemeColor = textColor || 'text.primary' // default to primary color text
      themeColorDisabled = 'light-gray-text-2.main'
      break
    case 'ghost-destructive':
      btnVariant = 'text'
      hoverColor = 'error.light'
      spinnerColor = 'error'
      bgColorDisabled = 'transparent'
      themeColor = 'error'
      themeColorDisabled = 'light-gray-text-2.main'
      break
    case 'underlined':
      btnVariant = 'text'
      hoverColor = 'transparent'
      spinnerColor = 'primary'
      bgColorDisabled = 'transparent'
      themeColor = 'primary'
      themeColorDisabled = 'light-gray-text-2.main'
      break
    case 'icon':
      btnVariant = 'text'
      hoverColor = 'primary.light'
      spinnerColor = 'primary'
      bgColorDisabled = 'transparent'
      themeColor = 'primary'
      textThemeColor = textColor || 'text.primary'
      themeColorDisabled = 'light-gray-text-2.main'
      break
    default: // same as contained
      btnVariant = 'contained'
      hoverColor = 'primary.hover'
      spinnerColor = 'white'
      themeColor = 'primary'
      themeColorDisabled = 'white.main'
  }

  const defaultProp = () => {
    const baseProps = {
      fontSize,
      fontWeight,
      minHeight,
      lineHeight: 1.25,
      boxShadow: 'none',
      borderRadius: variant === 'icon' ? '8px' : '500px',
      textTransform: 'none',

      px: size === 'large' ? '20px' : '16px',
      py: size === 'tiny' ? '4px' : '8px',

      color: textThemeColor,

      '& .MuiButton-startIcon': { marginLeft: 0 },
      '& .MuiButton-endIcon': { marginRight: 0 },

      '&.Mui-focusVisible': {
        outline: 'none',
        boxShadow: 'none',
      },

      '&:hover': {
        backgroundColor: hoverColor,
        boxShadow: 'none',
      },
      '&:disabled': {
        backgroundColor: bgColorDisabled,
        color: themeColorDisabled,
        boxShadow: 'none',
      },
    }


    // Icon variant specific styles
    if (variant === 'icon') {
      return {
        ...baseProps,
        minWidth: minHeight, // Make it square
        width: minHeight,
        height: minHeight,
        padding: 0, // Remove default padding for icons
      }
    }

    return baseProps
  }

  return (
    <Button
      onClick={onClick}
      variant={btnVariant}
      color={themeColor}
      disabled={disabled}
      {...rest}
      sx={{ ...defaultProp(), ...sx }}
    >
      {!isLoading ? children : <CircularProgress color={spinnerColor} size={fontSize} />}
    </Button>
  )
}

export default AdxButton
